import React, { Suspense, useCallback, useEffect, useState } from "react";

import Hamburger from "@urbanpiper-engineering/meraki-components/dist/Atoms/Hamburger";
import SearchCta from "@urbanpiper-engineering/meraki-components/dist/Molecules/SearchCta";
import Typography from "@urbanpiper-engineering/meraki-components/dist/Atoms/Typography";
import LanguageWidget from "@urbanpiper-engineering/meraki-components/dist/Molecules/LanguageWidget";
import NavDrawer from "@urbanpiper-engineering/meraki-components/dist/Cells/NavDrawer";
import Header from "@urbanpiper-engineering/meraki-components/dist/Cells/Header";
import HeaderLogo from "@urbanpiper-engineering/meraki-components/dist/Atoms/HeaderLogo";
import BottomNav from "@urbanpiper-engineering/meraki-components/dist/Cells/BottomNav";
import FloatingStack from "@urbanpiper-engineering/meraki-components/dist/Atoms/FloatingStack";
import useConfirm from "@urbanpiper-engineering/meraki-components/dist/Molecules/Confirm";
import configSelector from "../../external/configSelector";
import MerakiSDK from "@urbanpiper-engineering/meraki-sdk";

import { useRouter } from "next/router";
import ScreenHelper from "../../external/helpers/screenHelper";
import dynamic from "next/dynamic";
import { CartState } from "@urbanpiper-engineering/meraki-sdk/dist/esm/cart/states/cart.state";
import { useIsMobile } from "../../hooks/is_mobile.hook";
import { UserState } from "@urbanpiper-engineering/meraki-sdk/dist/esm/user/states/user.state";
import SideDrawerHelper from "../../external/helpers/sideDrawer.helper";
import deepClone from "../../external/helpers/deepClone";

import pageSelector from "../../external/pageSelector";
import { useTranslation } from "react-i18next";
import { LazyImage } from "../lazy-image";
import useFetchGoogleMapKey from "../../hooks/useFetchGoogleMapKey";
import { getHeaderColors } from "../../utils/themeColors";
import SortFilter from "@urbanpiper-engineering/meraki-components/dist/Molecules/SortFilter";
import { StoreState } from "@urbanpiper-engineering/meraki-sdk/dist/esm/stores/states/store.state";
import { EVENTS, PubSub } from "../../utils/listeners";
import useLanguage from "../../hooks/useLanguage.hook";

import styles from "./header.module.scss";

const LocationSelector = dynamic(() => import("../location_selector"), {
  ssr: false,
});
const Login = dynamic(() => import("../login"), { suspense: true });
const ViewCart = dynamic(
  () =>
    import("@urbanpiper-engineering/meraki-components/dist/Molecules/ViewCart"),
  { suspense: true },
);

const { HeaderBase, HeaderLeft, HeaderRight } = Header;

function HeaderComponent({
  webBgImg,
  supportedLanguages,
  businessInfo,
  locale,
  theme,
}: {
  webBgImg: string;
  supportedLanguages: string[];
  businessInfo: any;
  locale: string;
  theme: string;
}) {
  const googleApiKey =
    configSelector.getGoogleMapsKey() || configSelector.getDefaultMapsKey();
  const router = useRouter();

  const isMobileView = useIsMobile();
  const updateLocalLanguage = useLanguage();
  const [menuLinks, setMenuLinks] = useState(SideDrawerHelper.getLinks());

  const useCartState = MerakiSDK.getCartState().hook();
  const useUserState = MerakiSDK.getUserState().hook();
  const useStoreState = MerakiSDK.getStoreState().hook();

  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [sortFilter, setSortFilter] = useState<any>({});
  const [keyPresent, setKeyPresent] = useState(false);
  const [selectedSortOptions, setSelectedSortOptions] = useState<any>(null);
  const [selectedfilterOptions, setSelectedfilterOptions] = useState<any>([]);
  const [filterSortIndicatorData, setFilterSortIndicatorData] = useState<{
    showFilterIndicator: boolean;
    showSortIndicator: boolean;
  }>({ showFilterIndicator: false, showSortIndicator: false });
  const [defaultMapkey, setDefaultMapKey] = useState("");
  const fetchMapKey = useFetchGoogleMapKey(googleApiKey);

  const isFFSelectorVisible = ScreenHelper.isFFSelectorVisible(router.pathname);

  const isSearchVisible = ScreenHelper.isSearchVisible(router.pathname);
  let isMenuPage;
  const result = ScreenHelper.isMenuPage(router.pathname);
  if (Array.isArray(result) && result.length > 0) {
    isMenuPage = true;
  } else {
    isMenuPage = result;
  }
  const isLandingPage = ScreenHelper.isLandingPage(router.pathname);
  const isSearchPage = ScreenHelper.isSearchPage(router.pathname);
  const isCheckoutPage = ScreenHelper.isCheckoutPage(router.pathname);
  const isPaymentOptionsPage = ScreenHelper.isPaymentOptionsPage(
    router.pathname,
  );

  const userData = useUserState((state: typeof UserState) => state.user);
  const activeLanguageInState = useUserState(
    (state: typeof UserState) => state.language,
  );
  const setLanguage = useUserState(
    (state: typeof UserState) => state.setLanguage,
  );

  const cartCount = useCartState(
    (state: typeof CartState) => state.cart?.item_count,
  );

  const selectedStore = useStoreState(
    (state: typeof StoreState) => state.selectedStore,
  );

  const logo = configSelector.getBizLogoURL();
  const mobileLogo = configSelector.getBizSquareLogoURLMobile();
  const { showItemSearch, showItemSort, showItemFilter } =
    pageSelector.getMenuPage().customizations;

  const getUserDetails = (userData: any) => {
    if (userData) {
      return {
        name: [userData?.first_name, userData?.last_name].join(" "),
        phone: userData?.phone,
        is_guest_checkout: userData?.is_guest_checkout,
      };
    }
    return null;
  };

  useEffect(() => {
    fetchMapKey(setDefaultMapKey, setKeyPresent);
  }, []);

  useEffect(() => {
    setSelectedSortOptions(null);
    setSelectedfilterOptions([]);
  }, [isMenuPage, selectedStore?.id]);

  useEffect(() => {
    MerakiSDK.getSortFilters().then((_sortFilter: any) => {
      const _updatedSortFilter: any = {};

      if (!_sortFilter) return;

      if (showItemSort) {
        _updatedSortFilter["sort_by"] = _sortFilter["sort_by"];
      }
      if (showItemFilter) {
        _updatedSortFilter["filters"] = _sortFilter["filters"];
      }
      setSortFilter(_updatedSortFilter);
    });
  }, [showItemSort, showItemFilter]);

  useEffect(() => {
    if (openDrawer) {
      setOpenDialog(false);
    }
  }, [openDrawer]);

  useEffect(() => {
    if (openDialog) {
      setOpenDrawer(false);
    }
  }, [openDialog]);

  useEffect(() => {
    if (!menuLinks) return;
    const menuLinksClone = deepClone(menuLinks);
    if (userData && !userData?.is_guest_checkout) {
      menuLinksClone.forEach((links: any) => {
        links.isEnabled = true;
      });
    } else {
      menuLinksClone.forEach((links: any) => {
        if (links.isProtected) {
          links.isEnabled = false;
        }

        // show logout option for guest user
        if (
          links.label === "Logout" &&
          links.isProtected &&
          userData?.is_guest_checkout
        ) {
          links.isEnabled = true;
        }
      });
    }
    setMenuLinks(menuLinksClone);

    updateFreshChatUserInfo(userData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const updateFreshChatUserInfo = async (userData: any) => {
    const freshChatService = await MerakiSDK.getFreshChat();
    if (userData) {
      const userDetails = {
        firstName: userData?.first_name || "",
        lastName: userData?.last_name || "",
        phone: userData?.phone || "",
        email: userData?.email || "",
      };
      freshChatService.updateInfo(userDetails);
    } else {
      freshChatService.updateInfo({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      });
    }
  };

  const supportedLanguagesArr = supportedLanguages?.map((lang: string) => ({
    code: lang,
    name: lang.toUpperCase(),
  }));

  const [backButton, setBackButton] = useState(false);

  useEffect(() => {
    setBackButton(!ScreenHelper.isMenuPage(router.pathname));
  }, [router.pathname]);

  const { t } = useTranslation();
  const confirmationDialog = useConfirm();

  const onSortClearCallback = useCallback(() => {
    setSelectedSortOptions(null);
    onFilterByCallback(null, selectedfilterOptions);
    setFilterSortIndicatorData({
      showFilterIndicator: selectedfilterOptions?.length > 0,
      showSortIndicator: false,
    });
  }, [selectedfilterOptions]);

  const handleLogoutClick = async () => {
    setOpenDrawer(false);
    confirmationDialog({
      title: t("common.logoutAlertTitle"),
      content: t("common.logoutAlert"),
      removeCloseIcon: false,
      confirmText: t("header.logout"),
      variant: "delete",
      onConfirm: async () => {
        await MerakiSDK.logout();
        router.push("/menu");
      },
    });
  };

  const onFilterClearCallback = useCallback(() => {
    setSelectedfilterOptions([]);
    onFilterByCallback(selectedSortOptions, []);
    setFilterSortIndicatorData({
      showFilterIndicator: false,
      showSortIndicator: selectedSortOptions?.length > 0,
    });
  }, [selectedSortOptions]);

  const onFilterByCallback = (
    selectedSortOptions: any,
    selectedfilterOptions: any,
  ) => {
    PubSub.getInstance().emit(EVENTS.APPLY_FILTER_CALLBACK, {
      selectedSortOptions,
      selectedfilterOptions,
    });
    setSelectedfilterOptions(selectedfilterOptions);
    setSelectedSortOptions(selectedSortOptions);
    setFilterSortIndicatorData({
      showFilterIndicator: selectedfilterOptions?.length > 0,
      showSortIndicator: selectedSortOptions?.length > 0,
    });
  };

  /**
   * Todo
   * 1. Handle this in the header itself
   */
  if (isMobileView && isSearchPage && keyPresent) {
    return (
      <LocationSelector
        defaultMapsKey={defaultMapkey}
        hideLocationSelectorHeading
      />
    );
  }

  if (isLandingPage) {
    return;
  }

  return (
    <HeaderBase classes={styles.header} themeColors={getHeaderColors()}>
      <HeaderLeft>
        {backButton}
        <Hamburger
          onClick={() => {
            if (backButton) {
              //to avoid query history stack, specific category scroll
              if (isSearchPage) {
                return router.push("/menu");
              }

              return router.back();
            }
            setOpenDrawer(true);
          }}
          backButton={backButton}
          themeColors={getHeaderColors()}
        />

        <NavDrawer
          classes={styles.navDrawer}
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          user={getUserDetails(userData)}
          onLoginCallback={() => setOpenDialog(true)}
          menuLinks={menuLinks}
          onLogoutCallback={handleLogoutClick}
          onEditProfileCallback={() => {
            setOpenDrawer(false);
            router.push("/profile");
          }}
          isMobile={isMobileView}
          supportedLanguages={supportedLanguagesArr}
          activeLanguage={activeLanguageInState}
          onLanguageChange={updateLocalLanguage}
        />
        {!(backButton && isMobileView) && (
          <a
            className={styles.logoWrapper}
            onClick={() => router.push("/menu")}
          >
            <HeaderLogo
              imageComponent={LazyImage}
              webLogo={logo}
              mobileLogo={mobileLogo}
              isMobile={isMobileView}
            />
          </a>
        )}
        {isFFSelectorVisible && keyPresent && (
          <LocationSelector defaultMapsKey={defaultMapkey} />
        )}
      </HeaderLeft>

      {isMobileView && backButton ? (
        <Typography
          classes={styles.mobileHeaderTitle}
          color={getHeaderColors().textColor}
          variant="headingTwo"
        >
          {ScreenHelper.getTitle(router.pathname, router.asPath)}
        </Typography>
      ) : null}

      <HeaderRight classes={styles.headerWrapper}>
        {isSearchVisible && showItemSearch && (
          <SearchCta
            isActive={isSearchPage}
            isMobile={isMobileView}
            onClick={() => router.push("/search")}
            themeColors={getHeaderColors()}
          />
        )}

        {theme === "luna" ? (
          <>
            {supportedLanguages?.length > 1 && !isMobileView ? (
              <LanguageWidget
                onLanguageChange={(lang: string) => updateLocalLanguage(lang)}
                activeLanguage={
                  activeLanguageInState || supportedLanguagesArr[0].code
                }
                languages={supportedLanguagesArr}
                isMobile={isMobileView}
                themeColors={getHeaderColors()}
              />
            ) : null}
          </>
        ) : (
          <>
            {isMenuPage ? (
              <SortFilter
                sortOptions={sortFilter?.sort_by}
                filterOptions={sortFilter?.filters}
                onFilterByCallback={onFilterByCallback}
                onFilterClearCallback={onFilterClearCallback}
                onSortClearCallback={onSortClearCallback}
                selectedfilterOptions={selectedfilterOptions}
                selectedSortOptions={selectedSortOptions}
                filterSortIndicatorData={filterSortIndicatorData}
                themeColors={getHeaderColors()}
              />
            ) : null}
          </>
        )}
        <Suspense fallback={<div style={{ width: "68px", height: "40px" }} />}>
          <Login
            webBgImg={webBgImg}
            openDialog={openDialog}
            currency={businessInfo?.currency}
            isdCode={businessInfo?.isd_code}
          />
        </Suspense>
        <Suspense fallback={<div style={{ width: "68px", height: "40px" }} />}>
          {!isMobileView && (
            <a onClick={() => cartCount > 0 && router.push("/checkout")}>
              <ViewCart
                themeColors={getHeaderColors()}
                numberOfCartItems={cartCount}
                isMobile={isMobileView}
              />
            </a>
          )}
        </Suspense>
      </HeaderRight>
      {isMobileView && !isPaymentOptionsPage && !isCheckoutPage ? (
        <FloatingStack order={-1}>
          <BottomNav
            items={[
              {
                type: "home",
                label: "home",
                link: "/menu",
              },
              {
                type: "offers",
                label: "offers",
                link: "/offers",
                disabled: userData?.is_guest_checkout,
              },
              {
                type: "orders",
                label: "orders",
                link: "/orders",
                disabled: userData?.is_guest_checkout,
              },
              {
                type: "profile",
                title: userData?.first_name,
                link: "/profile",
                label: "profile",
              },
            ]}
            activePath={router.pathname}
            onNavUpdate={(path: string) => {
              if (path === "/profile") {
                setOpenDrawer(true);
              } else {
                router.push(path);
              }
            }}
          />
        </FloatingStack>
      ) : null}
    </HeaderBase>
  );
}

export default HeaderComponent;
